import { Newspaper, Search, Loader2, LogIn, ChevronDown, Bookmark } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ThemeToggle } from '@/components/ThemeToggle'
import { KeywordInput } from './KeywordInput'
import { PresetKeywordButtons } from './PresetKeywordButtons'
import { SearchSettings } from './SearchSettings'
import { DigestSettings } from './DigestSettings'
import { GroupManager } from './GroupManager'
import { SearchHistory } from './SearchHistory'
import { useAuth, UserProfile } from '@/features/auth'

/**
 * Cap for a visitor with no account.
 *
 * Not a tier limit: keywords live in localStorage until someone signs in, so the
 * server never sees them and no tier applies. The real ceiling here is the request
 * schema's `max_length=20` (`backend/app/schemas/user_preferences.py`), which is what
 * they meet the moment they do sign in and save.
 */
const ANONYMOUS_KEYWORD_LIMIT = 20

interface SidebarProps {
  registeredKeywords: string[]
  onRegisteredKeywordsChange: (keywords: string[]) => void
  searchErrors: string[]
  isPending: boolean
  onSearch: () => void
  onSearchGroups: (keywords: string[]) => void
  onSelectHistory: (keywords: string[], params: { display: number; sort: string }) => void
  onOpenAuthModal: () => void
  onSelectPreset: (keywords: string[]) => void
}

/**
 * Sidebar component containing all search controls and settings
 * Manages keyword input, group management, settings, and search history
 */
export function Sidebar({
  registeredKeywords,
  onRegisteredKeywordsChange,
  searchErrors,
  isPending,
  onSearch,
  onSearchGroups,
  onSelectHistory,
  onOpenAuthModal,
  onSelectPreset,
}: SidebarProps) {
  const { isAuthenticated, user } = useAuth()

  // The server enforces this per tier (`apply_writes`); hardcoding 20 here meant a
  // Free account typed freely to 20 and was refused at the 6th with a 403.
  const keywordLimit = user?.preferences.keyword_limit ?? ANONYMOUS_KEYWORD_LIMIT

  return (
    // Positioning belongs to NewsLayout's <aside>; this is just the content column.
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-lg bg-primary p-2">
          <Newspaper className="h-6 w-6 text-primary-foreground" />
        </div>
        <h1 className="flex-1 text-xl font-bold sm:text-2xl">헤드라이너</h1>
        <ThemeToggle />
        {isAuthenticated ? (
          <UserProfile />
        ) : (
          <Button variant="ghost" size="icon" onClick={onOpenAuthModal} aria-label="로그인">
            <LogIn className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/*
        내가 만든 클리핑으로 돌아오는 유일한 입구다. 로그인한 사람에게만 보인다 —
        비로그인 상태에서 만든 클리핑은 주인이 없어 목록에 담기지 않으므로
        (`Clip.created_by`), 링크를 걸어 봐야 빈 화면으로 데려간다.
      */}
      {isAuthenticated && (
        <div className="mb-4">
          <Button variant="outline" size="sm" className="w-full justify-start" asChild>
            <Link to="/clips">
              <Bookmark className="mr-2 h-4 w-4" />내 클리핑
            </Link>
          </Button>
        </div>
      )}

      {/* Presets — always reachable, one click to switch topic */}
      <div className="mb-4">
        <PresetKeywordButtons onSelectPreset={onSelectPreset} variant="compact" />
      </div>

      {/* Keyword Input */}
      <div className="mb-4">
        <KeywordInput
          maxKeywords={keywordLimit}
          registeredKeywords={registeredKeywords}
          onRegisteredKeywordsChange={onRegisteredKeywordsChange}
        />
      </div>

      {/* Search Button */}
      <div className="mb-6">
        <Button onClick={onSearch} disabled={isPending} className="w-full" size="lg">
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              검색 중...
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              검색하기
            </>
          )}
        </Button>
      </div>

      {/*
        Secondary controls. Below `lg` these stack on top of the results, so a phone
        user had to scroll past ~700px of settings to reach the first news card.
        They collapse into a native <details> there and stay expanded on desktop —
        no JavaScript, no state to keep in sync.
      */}
      <details className="group mb-6 lg:hidden" name="sidebar-section">
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-md border px-3 text-sm font-medium">
          검색 도구
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="mt-4 space-y-6">
          <GroupManager onSearchGroups={onSearchGroups} />
          <SearchSettings />
          <DigestSettings keywords={registeredKeywords} />
          <SearchHistory onSelectHistory={onSelectHistory} />
        </div>
      </details>

      <div className="mb-6 hidden space-y-6 lg:block">
        <GroupManager onSearchGroups={onSearchGroups} />
        <SearchSettings />
        <DigestSettings keywords={registeredKeywords} />
        <SearchHistory onSelectHistory={onSelectHistory} />
      </div>

      {/* Error Messages */}
      {searchErrors.length > 0 && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>
            <ul className="list-inside list-disc space-y-1">
              {searchErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/*
        Site footer. The app has no other chrome, so the legally required links live
        here. Plain <a>, not a router link: /privacy and /terms are static HTML in
        public/, outside the SPA, so a client-side navigation would never reach them.
      */}
      <nav aria-label="약관" className="mt-8 border-t pt-4 text-xs text-muted-foreground">
        <a href="/privacy/" className="hover:underline">
          개인정보처리방침
        </a>
        <span aria-hidden="true"> · </span>
        <a href="/terms/" className="hover:underline">
          이용약관
        </a>
        <span aria-hidden="true"> · </span>
        <a href="/pricing/" className="hover:underline">
          요금제
        </a>
      </nav>
    </div>
  )
}
